/**
 * 2026-05-25 — local-only demo "AI news pulse" widget used in the
 * experimental crypto-price-style market view to iterate on the
 * news-summary UX. Pattern: short LLM-style summary at the top of
 * the news rail with source attribution chips below + a relative
 * "updated" timestamp. Hardcoded fixture; no API call. Replace
 * with a real /v1/markets/:slug/news-summary endpoint once the UX
 * is locked.
 */
"use client";

import { useState } from "react";
import { Sparkles, ExternalLink } from "lucide-react";

interface Source {
  domain: string;
  headline: string;
  postedMinutesAgo: number;
}

const SOURCES: Source[] = [
  { domain: "Bloomberg", headline: "Spot BTC ETFs see $1.2B weekly inflow", postedMinutesAgo: 38 },
  { domain: "Reuters", headline: "Fed signals patience on rate cuts through Q3", postedMinutesAgo: 92 },
  { domain: "CoinDesk", headline: "Long-term holder supply hits new ATH", postedMinutesAgo: 156 },
  { domain: "FT", headline: "Treasury yields ease as inflation prints in line", postedMinutesAgo: 248 },
];

const SUMMARY =
  "ETF flows ran ~4× new BTC issuance in May with spot demand concentrated in the $98–110K band. Long-term holder supply hit a fresh all-time high, while macro tape stayed quiet — Fed kept its patient-on-cuts framing and Treasury yields drifted lower. Net read: bid is structural, but no near-term catalyst pulls price toward $200K without a vol expansion event.";

export function DemoAiNewsSummary() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="text-[14px]" style={{ color: "#0A0B0D" }}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full"
            style={{ background: "rgba(22, 82, 240, 0.10)" }}
          >
            <Sparkles size={14} strokeWidth={1.75} style={{ color: "#1652F0" }} />
          </span>
          <h2 className="text-[20px] font-semibold tracking-[-0.01em]">
            News pulse
          </h2>
          <span
            className="rounded-full px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.06em]"
            style={{ background: "rgba(22, 82, 240, 0.08)", color: "#1652F0" }}
          >
            AI
          </span>
        </div>
        <span className="text-[12px]" style={{ color: "#8A919E" }}>
          Updated 4m ago
        </span>
      </div>

      <p
        className="text-[15px] leading-[1.55]"
        style={{ color: "#0A0B0D" }}
      >
        {SUMMARY}
      </p>

      <div
        className="mt-4 border-t pt-4"
        style={{ borderColor: "#ECEFF2" }}
      >
        <div
          className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: "#0A0B0D" }}
        >
          Sources · {SOURCES.length}
        </div>
        <ul
          className="flex flex-col gap-2 overflow-hidden transition-[max-height] duration-300 ease-out"
          style={{
            maxHeight: expanded ? "800px" : "44px",
            WebkitMaskImage: expanded
              ? "none"
              : "linear-gradient(to bottom, black 0%, transparent 100%)",
            maskImage: expanded
              ? "none"
              : "linear-gradient(to bottom, black 0%, transparent 100%)",
          }}
        >
          {SOURCES.map((s, i) => (
            <li key={i}>
              <a
                href="#"
                className="group flex items-start gap-2 rounded-md p-1 transition-colors hover:bg-[#F7F8FA]"
              >
                <span
                  className="mt-0.5 inline-flex flex-shrink-0 items-center rounded-md px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.04em]"
                  style={{ background: "#F7F8FA", color: "#5B616E" }}
                >
                  {s.domain}
                </span>
                <span
                  className="min-w-0 flex-1 truncate text-[13px] leading-[1.4]"
                  style={{ color: "#0A0B0D" }}
                >
                  {s.headline}
                </span>
                <span
                  className="flex-shrink-0 text-[11px] tabular-nums"
                  style={{ color: "#8A919E" }}
                >
                  {s.postedMinutesAgo < 60
                    ? `${s.postedMinutesAgo}m`
                    : `${Math.floor(s.postedMinutesAgo / 60)}h`}
                </span>
                <ExternalLink
                  size={12}
                  strokeWidth={1.75}
                  className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  style={{ color: "#8A919E" }}
                />
              </a>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-[12px] font-semibold underline-offset-2 hover:underline"
          style={{ color: "#5B616E" }}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      </div>
    </div>
  );
}
